import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { fileToDataUrl } from "@/lib/sales";
import {
  UserPlus, Wallet, Users, ClipboardCheck, CalendarClock,
  Loader2, CheckCircle2, Upload, FileText, ArrowLeft, KeyRound,
} from "lucide-react";

// Interazzo — CRM streamlined intake (Feb 2026).
// Skips Sales/Design/Ops. CRM enters client details, project amount, timeline
// start date, optional overrides, and design files → single POST to
// /api/crm/direct-onboard creates design_project + timeline + client account.

const TIMELINE_POINTS = [
  { key: "kickstart_production", label: "Kickstart of Production Work" },
  { key: "civil_and_electrical", label: "Floor Covering, Civil, False Ceiling & Electrical" },
  { key: "factory_production_completion", label: "Expected Factory Production Completion" },
  { key: "dispatch_to_site", label: "Dispatch to Site" },
  { key: "carcass_assembly_commences", label: "Carcass Assembly at Site Commences" },
  { key: "carcass_assembly_completion", label: "Completion of Carcass Assembly" },
  { key: "next_45_payment_intercept", label: "Next 45% Payment Milestone Intercept" },
  { key: "site_customization_commences", label: "Site Manual Customization Work Commences" },
  { key: "doors_measurement_start", label: "Doors Measurement, Prep & Hardware Procurement Start" },
  { key: "doors_manufacturing_completion", label: "Doors Manufacturing Completion at Factory" },
  { key: "shutter_packing_shifting", label: "Packing & Shifting of Shutter Materials + Hardware" },
  { key: "shutter_assembly_commences", label: "Assembly of Shutters Commences at Site" },
  { key: "final_completion_handover", label: "Final Project Completion & Handover" },
];

// Same offset math as backend TIMELINE_POINTS. Kept in-sync manually — the
// backend is source of truth; this preview is just visual guidance.
function offsetFor(key, X) {
  switch (key) {
    case "kickstart_production": return 0;
    case "civil_and_electrical": return X / 2 - 10;
    case "factory_production_completion": return X / 2;
    case "dispatch_to_site": return X / 2 + 2;
    case "carcass_assembly_commences": return X / 2 + 0.1 * X + 3;
    case "carcass_assembly_completion": return X / 2 + 0.2 * X + 3;
    case "next_45_payment_intercept": return X / 2 + 0.2 * X + 4;
    case "site_customization_commences": return X / 2 + 0.2 * X + 5;
    case "doors_measurement_start": return X / 2 + 0.2 * X + 5;
    case "doors_manufacturing_completion": return X / 2 + 0.2 * X + 9;
    case "shutter_packing_shifting": return X / 2 + 0.2 * X + 10;
    case "shutter_assembly_commences": return X / 2 + 0.2 * X + 11;
    case "final_completion_handover": return X;
    default: return 0;
  }
}
function computeX(amount) {
  const l = (amount || 0) / 100000;
  if (l >= 18 && l <= 25) return 90;
  if (l >= 26 && l <= 30) return 105;
  if (l >= 31 && l <= 40) return 120;
  if (l >= 41 && l <= 50) return 135;
  if (l >= 51 && l <= 60) return 150;
  if (l >= 61 && l <= 80) return 165;
  if (l >= 81 && l <= 100) return 180;
  if (l < 18) return 90;
  return 180;
}
// Add N working days (skip Sundays only for the preview — server also skips
// India national holidays, so the actual stored dates may shift by 1-2 days).
function addWorkingDays(iso, n) {
  if (!iso) return "";
  const d = new Date(iso);
  let remaining = Math.round(n);
  while (remaining > 0) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0) remaining -= 1; // 0 = Sunday
  }
  return d.toISOString().slice(0, 10);
}

const FILE_SLOTS = [
  { key: "final_2d", label: "Final 2D Drawings" },
  { key: "final_3d", label: "Final 3D Renders" },
  { key: "quotation", label: "Quotation PDF" },
  { key: "signoff_sheet", label: "Sign-off Sheet" },
];

export default function DirectOnboardPage() {
  const navigate = useNavigate();
  const [designers, setDesigners] = useState([]);
  const [opsManagers, setOpsManagers] = useState([]);
  const [opsExecs, setOpsExecs] = useState([]);
  const [projectManagers, setProjectManagers] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // {temp_password, client_email, project_id}
  // Feb 2026 · City scoping. `strict === true` → CRM Executive with an
  // `assigned_cities` list; must be rendered as a hard `<select>` (no
  // free-text). `strict === false` → Admin/Manager/Director; render as a
  // combobox with suggestions and let them type new cities too.
  const [cityOptions, setCityOptions] = useState([]);
  const [cityStrict, setCityStrict] = useState(false);

  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    client_address: "",
    client_city: "",
    project_name: "",
    project_amount: "",
    booking_amount: "",
    interior_designer_id: "",
    operations_manager_id: "",
    operations_executive_id: "",
    project_manager_id: "",
    site_supervisor_id: "",
    timeline_start_date: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [overrides, setOverrides] = useState({}); // { key: "YYYY-MM-DD" }
  const [files, setFiles] = useState({}); // { key: data_url }

  useEffect(() => {
    const load = async () => {
      const roles = [
        // Feb 2026: Designer dropdown pools users from ALL three design roles
        // so a project can be assigned directly to a Design Manager or Design
        // Head when they are handling it themselves.
        ["designers", ["Interior Designer", "Design Manager", "Design Head"], setDesigners],
        ["opsMgrs",   ["Operations Manager"],    setOpsManagers],
        ["opsExecs",  ["Operations Executive"],  setOpsExecs],
        ["pms",       ["Project Manager"],       setProjectManagers],
        ["sups",      ["Site Supervisor"],       setSupervisors],
      ];
      await Promise.all(roles.map(async ([_key, roleList, setter]) => {
        try {
          const results = await Promise.all(
            roleList.map((r) => api.get(`/users/by-role/${encodeURIComponent(r)}`)
                                    .then((res) => (res.data || []).map((u) => ({ ...u, role_label: r })))
                                    .catch(() => []))
          );
          // Merge, dedupe by user id (someone could theoretically hold multiple roles)
          const merged = [];
          const seen = new Set();
          for (const list of results) {
            for (const u of list) {
              if (!seen.has(u.id)) { seen.add(u.id); merged.push(u); }
            }
          }
          merged.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
          setter(merged);
        } catch { setter([]); }
      }));
    };
    load();
  }, []);

  // Feb 2026 · CRM Executive city scoping — fetch allowed city options.
  useEffect(() => {
    api.get("/crm/known-cities")
      .then(({ data }) => {
        setCityOptions(data.cities || []);
        setCityStrict(!!data.strict);
      })
      .catch(() => { /* Fall back to free-text on failure. */ });
  }, []);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setOverride = (k, v) => setOverrides(o => ({ ...o, [k]: v }));

  // Live-preview 13 dates from start + amount
  const preview = useMemo(() => {
    const X = computeX(Number(form.project_amount) || 0);
    return TIMELINE_POINTS.map(p => ({
      ...p,
      auto: addWorkingDays(form.timeline_start_date, offsetFor(p.key, X)),
    }));
  }, [form.timeline_start_date, form.project_amount]);

  const onFile = async (key, fileList) => {
    if (!fileList?.[0]) return;
    try {
      const dataUrl = await fileToDataUrl(fileList[0]);
      setFiles(f => ({ ...f, [key]: dataUrl }));
    } catch (e) {
      toast.error("Could not read file");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.client_email || !form.client_phone) {
      toast.error("Client name, email and phone are required.");
      return;
    }
    if (!form.project_name || !form.project_amount) {
      toast.error("Project name and amount are required.");
      return;
    }
    if (!form.timeline_start_date) {
      toast.error("Timeline start date is required.");
      return;
    }
    if (cityStrict) {
      if (!form.client_city) {
        toast.error("Please pick a city from your assigned list.");
        return;
      }
      if (!cityOptions.some((c) => c.toLowerCase() === form.client_city.toLowerCase())) {
        toast.error("Selected city is outside your assigned cities.");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        client_name: form.client_name.trim(),
        client_email: form.client_email.trim().toLowerCase(),
        client_phone: form.client_phone.trim(),
        client_address: form.client_address || null,
        client_city: form.client_city || null,
        project_name: form.project_name.trim(),
        project_amount: Number(form.project_amount),
        booking_amount: form.booking_amount === "" ? 0 : Number(form.booking_amount),
        interior_designer_id: form.interior_designer_id || null,
        operations_manager_id: form.operations_manager_id || null,
        operations_executive_id: form.operations_executive_id || null,
        project_manager_id: form.project_manager_id || null,
        site_supervisor_id: form.site_supervisor_id || null,
        timeline_start_date: form.timeline_start_date,
        timeline_overrides: Object.entries(overrides)
          .filter(([, v]) => v)
          .map(([key, date]) => ({ key, date })),
        final_2d: files.final_2d || null,
        final_3d: files.final_3d || null,
        quotation: files.quotation || null,
        signoff_sheet: files.signoff_sheet || null,
        notes: form.notes || null,
      };
      const { data } = await api.post("/crm/direct-onboard", payload);
      setResult(data);
      toast.success("Client onboarded · Portal invite dispatched");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (result) {
    return (
      <SuccessCard result={result} onDone={() => navigate("/crm/onboarding")} />
    );
  }

  return (
    <div className="min-h-full pb-16" data-testid="crm-direct-onboard-page">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <button onClick={() => navigate("/crm/onboarding")}
                className="text-[10px] font-mono uppercase tracking-widest text-gray-500 hover:text-[#d4af37] flex items-center gap-1 mb-2"
                data-testid="back-to-onboarding">
          <ArrowLeft className="w-3 h-3"/> Back to Onboarding
        </button>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">CRM · Direct Intake</p>
        <h1 className="font-display text-3xl text-white mt-1">Onboard a New Client</h1>
        <p className="text-xs text-gray-500 font-mono mt-1">
          Skips Sales / Design / Ops. Creates the client account, the 13-line timeline, and unlocks the client portal in one step.
        </p>
      </div>

      <form onSubmit={submit} className="px-10 py-8 max-w-6xl mx-auto space-y-6">
        {/* Client card */}
        <SectionCard icon={UserPlus} title="Client Details" testid="section-client">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField label="Client Name *" value={form.client_name} onChange={v => setField("client_name", v)} testid="direct-client-name"/>
            <TextField label="Client Email *" value={form.client_email} onChange={v => setField("client_email", v)} type="email" testid="direct-client-email"/>
            <TextField label="Client Phone *" value={form.client_phone} onChange={v => setField("client_phone", v)} testid="direct-client-phone"/>
            <CityPicker
              label={cityStrict ? "City * (Assigned)" : "City"}
              value={form.client_city}
              onChange={(v) => setField("client_city", v)}
              options={cityOptions}
              strict={cityStrict}
            />
            <div className="md:col-span-2">
              <TextField label="Address" value={form.client_address} onChange={v => setField("client_address", v)} testid="direct-client-address"/>
            </div>
          </div>
        </SectionCard>

        {/* Project card */}
        <SectionCard icon={Wallet} title="Project Details" testid="section-project">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TextField label="Project Name *" value={form.project_name} onChange={v => setField("project_name", v)} testid="direct-project-name"/>
            <TextField label="Project Amount (₹) *" value={form.project_amount} onChange={v => setField("project_amount", v)} type="number" testid="direct-project-amount"/>
            <TextField label="Booking Amount (₹)" value={form.booking_amount} onChange={v => setField("booking_amount", v)} type="number" testid="direct-booking-amount"/>
          </div>
        </SectionCard>

        {/* Stakeholders card (all optional) */}
        <SectionCard icon={Users} title="Assign Team (Optional)" testid="section-team">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField label="Interior Designer" value={form.interior_designer_id}
                          onChange={v => setField("interior_designer_id", v)}
                          options={designers} testid="direct-designer"/>
            <SelectField label="Operations Manager" value={form.operations_manager_id}
                          onChange={v => setField("operations_manager_id", v)}
                          options={opsManagers} testid="direct-ops-manager"/>
            <SelectField label="Operations Executive" value={form.operations_executive_id}
                          onChange={v => setField("operations_executive_id", v)}
                          options={opsExecs} testid="direct-ops-exec"/>
            <SelectField label="Project Manager" value={form.project_manager_id}
                          onChange={v => setField("project_manager_id", v)}
                          options={projectManagers} testid="direct-project-manager"/>
            <SelectField label="Site Supervisor" value={form.site_supervisor_id}
                          onChange={v => setField("site_supervisor_id", v)}
                          options={supervisors} testid="direct-site-supervisor"/>
          </div>
        </SectionCard>

        {/* Timeline card */}
        <SectionCard icon={CalendarClock} title="13-Line Production Timeline" testid="section-timeline">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <TextField label="Timeline Start Date *" type="date"
                        value={form.timeline_start_date}
                        onChange={v => setField("timeline_start_date", v)}
                        testid="direct-start-date"/>
            <div className="md:pt-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                Auto-computed from start date + amount (skips Sundays &amp; holidays).
                <br/>Override any row below.
              </p>
            </div>
          </div>

          <div className="border border-[#262626] rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="direct-timeline-table">
              <thead>
                <tr className="bg-[#0f0f0f] border-b border-[#262626]">
                  {["#", "Milestone", "Auto Date", "Override (optional)"].map(h => (
                    <th key={h} className="text-[10px] font-mono uppercase tracking-widest text-gray-500 py-3 px-4 text-left">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((row, idx) => (
                  <tr key={row.key} className="border-b border-[#1a1a1a]" data-testid={`direct-timeline-row-${row.key}`}>
                    <td className="py-2.5 px-4 text-xs text-gray-500 font-mono">{idx + 1}</td>
                    <td className="py-2.5 px-4 text-white text-sm">{row.label}</td>
                    <td className="py-2.5 px-4 text-[#d4af37] font-mono text-xs">{row.auto || "—"}</td>
                    <td className="py-2.5 px-4">
                      <input type="date"
                              value={overrides[row.key] || ""}
                              onChange={(e) => setOverride(row.key, e.target.value)}
                              className="iz-input text-xs !py-1 !px-2 max-w-[160px]"
                              data-testid={`direct-timeline-override-${row.key}`}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>

        {/* Files card */}
        <SectionCard icon={ClipboardCheck} title="Final Design Files (Optional)" testid="section-files">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FILE_SLOTS.map(slot => (
              <FileSlot key={slot.key} slot={slot} value={files[slot.key]} onChange={onFile}/>
            ))}
          </div>

          <div className="mt-4">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Notes</label>
            <textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)}
                       className="iz-input" rows={3} data-testid="direct-notes"/>
          </div>
        </SectionCard>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate("/crm/onboarding")}
                   className="iz-btn-secondary" disabled={saving} data-testid="direct-cancel">
            Cancel
          </button>
          <button type="submit" disabled={saving}
                   className="iz-btn-primary flex items-center gap-2" data-testid="direct-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
            {saving ? "Onboarding…" : "Onboard Client · Unlock Portal"}
          </button>
        </div>
      </form>
    </div>
  );
}

function SectionCard({ icon: Icon, title, testid, children }) {
  return (
    <section className="iz-card p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-4 h-4 text-[#d4af37]"/>}
        <h3 className="font-display text-lg text-white">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function TextField({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
              className="iz-input" data-testid={testid}/>
    </div>
  );
}

// Feb 2026 · CRM Executive city scoping. When `strict === true`, renders a
// hard `<select>` limited to `options` (no free text). Otherwise renders a
// text input backed by a `<datalist>` so managers still get suggestions but
// can type any city they need.
function CityPicker({ label, value, onChange, options, strict }) {
  if (strict) {
    return (
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">{label}</label>
        <select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="iz-input"
          data-testid="direct-client-city"
        >
          <option value="">-- Select an assigned city --</option>
          {options.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        {options.length === 0 && (
          <p className="text-[10px] font-mono uppercase tracking-widest text-red-400 mt-1"
              data-testid="direct-client-city-empty-warning">
            No cities assigned · contact admin
          </p>
        )}
      </div>
    );
  }
  const listId = "direct-city-suggestions";
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">{label}</label>
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="iz-input"
        list={listId}
        placeholder={options.length ? "Start typing…" : ""}
        data-testid="direct-client-city"
      />
      <datalist id={listId}>
        {options.map((c) => (<option key={c} value={c}/>))}
      </datalist>
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">{label}</label>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}
               className="iz-input" data-testid={testid}>
        <option value="">-- Not assigned --</option>
        {options.map((u) => (
          <option key={u.id} value={u.id}>
            {u.role_label
              ? `${u.name} · ${u.role_label} · ${u.email}`
              : `${u.name} · ${u.email}`}
          </option>
        ))}
      </select>
    </div>
  );
}

function FileSlot({ slot, value, onChange }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">{slot.label}</label>
      <label className="iz-btn-secondary inline-flex items-center gap-2 cursor-pointer w-full justify-center"
              data-testid={`direct-file-${slot.key}`}>
        {value ? <FileText className="w-3.5 h-3.5 text-emerald-400"/> : <Upload className="w-3.5 h-3.5"/>}
        {value ? "Ready to upload · click to replace" : "Choose file"}
        <input type="file" hidden accept="application/pdf,image/*,.dwg,.dxf"
                onChange={(e) => onChange(slot.key, e.target.files)}/>
      </label>
    </div>
  );
}

function SuccessCard({ result, onDone }) {
  return (
    <div className="min-h-full flex items-center justify-center px-6 py-16" data-testid="direct-success-card">
      <div className="iz-card p-10 max-w-lg w-full">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/40 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-[#d4af37]"/>
          </div>
        </div>
        <h2 className="font-display text-2xl text-white text-center mb-2">Client Onboarded</h2>
        <p className="text-xs font-mono text-gray-500 text-center mb-6">
          A welcome invite (mocked email + SMS) has been dispatched. Client can now log into the portal.
        </p>

        <div className="space-y-3">
          <CredentialRow label="Client Email" value={result.client_email}/>
          <CredentialRow label="Temporary Password" value={result.temp_password} icon={KeyRound}/>
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] text-center mt-4">
            Client will be prompted to reset password on first login.
          </p>
        </div>

        <div className="mt-8 flex justify-center">
          <button onClick={onDone} className="iz-btn-primary" data-testid="direct-success-done">
            Back to Onboarding
          </button>
        </div>
      </div>
    </div>
  );
}

function CredentialRow({ label, value, icon: Icon }) {
  return (
    <div className="border border-[#262626] rounded-md px-4 py-3 flex items-center justify-between">
      <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{label}</span>
      <span className="text-sm text-white font-mono flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#d4af37]"/>}
        {value}
      </span>
    </div>
  );
}
